import { useState, createContext, useEffect, useContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";

import { LayoutContext } from "../../../layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import {
  listHolidays,
  serviceOrdersByBranchID,
  serviceOrdersByCompanyID,
} from "graphql/queries";
import { createServiceOrderLog, updateServiceOrder } from "graphql/mutations";
import { serviceOrderStatus } from "config/constants";
import { useDebounce } from "hooks/useDebounce";
import { convertToSlug } from "components/utils/functions";

export const ScheduleContext = createContext({});

export const ScheduleProvider = ({ children, branchID, companyID }) => {
  const { me } = useContext(LayoutContext);
  const [draggedEvent, setDraggedEvent] = useState();
  const [selectedEvent, setSelectedEvent] = useState();
  const [debounceEvent, setDebounceEvent] = useState();
  const debounceSelectedEvent = useDebounce(debounceEvent, 500);
  const [availableEvents, setAvailableEvents] = useState({
    loading: false,
    list: [],
  });
  const [events, setEvents] = useState({
    loading: false,
    list: [],
  });

  const createEvent = ({ start, end, allDay: isAllDay }) => {
    const { id } = draggedEvent;
    const eventFiltered = availableEvents.list.find((ev) => ev.id === id);

    setDraggedEvent(null);
    const event = {
      ...eventFiltered,
      start,
      end,
      status: serviceOrderStatus.scheduled,
      isAllDay,
    };
    setEvents((prev) => ({ ...prev, list: [...prev.list, event] }));
    setAvailableEvents((prev) => ({
      ...prev,
      list: prev.list.filter((prevIt) => prevIt.id !== event.id),
    }));

    funcUpdateServiceOrder({
      id,
      start,
      end,
      status: serviceOrderStatus.scheduled,
    });
  };

  const deleteEvent = (id) => {
    const eventFiltered = events.list.find((ev) => ev.id === id);

    setAvailableEvents((prev) => ({
      ...prev,
      list: [...prev.list, eventFiltered],
    }));
    setEvents((prev) => ({
      ...prev,
      list: prev.list.filter((prevIt) => prevIt.id !== eventFiltered.id),
    }));
    setSelectedEvent(null);

    funcUpdateServiceOrder({
      id,
      start: null,
      end: null,
      status: serviceOrderStatus.created,
    });
  };

  const moveEvent = ({
    event,
    start,
    end,
    isAllDay: droppedOnAllDaySlot = false,
  }) => {
    const { allDay, id } = event;
    if (!allDay && droppedOnAllDaySlot) {
      event.allDay = true;
    }

    if (selectedEvent?.id === id) {
      setSelectedEvent((prev) => ({ ...prev, start, end }));
    }

    setEvents((prev) => {
      const existing = prev.list.find((ev) => ev.id === id) ?? {};
      const filtered = prev.list.filter((ev) => ev.id !== id);
      return {
        ...prev,
        list: [...filtered, { ...existing, start, end, allDay }],
      };
    });
    funcUpdateServiceOrder({ id, start, end });
  };

  const updateEvent = (event) => {
    const { id } = event;

    if (
      (event.hasOwnProperty("title") && !event.title) ||
      (event.hasOwnProperty("start") && !event.start) ||
      (event.hasOwnProperty("end") && !event.end)
    )
      return;

    if (
      event.end &&
      moment(event.end).isSameOrBefore(selectedEvent.start, "minute")
    ) {
      toast.error(strings.validations.endBeforeStart);
      return;
    }

    setSelectedEvent((prev) => ({ ...prev, ...event }));
    setEvents((prev) => {
      const existing = prev.list.find((ev) => ev.id === id) ?? {};
      const filtered = prev.list.filter((ev) => ev.id !== id);
      return {
        ...prev,
        list: [...filtered, { ...existing, ...event }],
      };
    });
    setDebounceEvent({ id, ...event });
  };

  const selectEvent = async (event) => {
    if (event?.isHoliday) setSelectedEvent(null);
    else setSelectedEvent(event);
  };

  const selectUserExecutor = async (id) => {
    setSelectedEvent((prev) => ({ ...prev, userExecutor: id }));

    setEvents((prev) => {
      const existing = prev.list.find((ev) => ev.id === selectedEvent.id) ?? {};
      const filtered = prev.list.filter((ev) => ev.id !== selectedEvent.id);
      return {
        ...prev,
        list: [...filtered, { ...existing, userExecutor: id }],
      };
    });
    funcUpdateServiceOrder({
      id: selectedEvent.id,
      userExecutor: id || null,
    });
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNames = (list, callback) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      callback((prev) => {
        const updatedList = [...prev.list];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          title: item.title || `${item.code} - ${companyName}`,
          companyName,
        };

        return { ...prev, list: updatedList };
      });
    });
  };

  const fetchAvailableEvents = async () => {
    let objOperation = {};
    let nameOperation = null;
    let nameOperationStr = "";
    try {
      setAvailableEvents({ loading: true, list: [] });

      if (!companyID && !me.companyID) return;

      if (!!branchID) {
        objOperation = {
          branchID: branchID,
        };
        nameOperation = serviceOrdersByBranchID;
        nameOperationStr = "serviceOrdersByBranchID";
      } else if (!!companyID || !!me.companyID) {
        objOperation = {
          companyID: companyID || me.companyID,
        };
        nameOperation = serviceOrdersByCompanyID;
        nameOperationStr = "serviceOrdersByCompanyID";
      }

      const serviceOrdersData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          filter: {
            deletedAt: { attributeExists: false },
            and: [
              { status: { ne: "executed" } },
              { status: { ne: "canceled" } },
              {
                or: [
                  { start: { attributeExists: false } },
                  { start: { attributeType: "_null" } },
                ],
              },
              {
                or: [
                  { end: { attributeExists: false } },
                  { end: { attributeType: "_null" } },
                ],
              },
            ],
          },
        })
      );
      const serviceOrdersItems = serviceOrdersData.data[nameOperationStr].items;
      setAvailableEvents({
        loading: false,
        list: serviceOrdersItems.map((serv) => ({
          ...serv,
          title: serv.title || serv.code,
        })),
      });
      getNames(serviceOrdersItems, setAvailableEvents);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setAvailableEvents((prev) => ({ ...prev, loading: false }));
    }
  };

  const fetchEvents = async () => {
    let objOperation = {};
    let nameOperation = null;
    let nameOperationStr = "";
    try {
      setEvents({ loading: true, list: [] });

      if (!companyID && !me.companyID) return;

      if (!!branchID) {
        objOperation = {
          branchID: branchID,
        };
        nameOperation = serviceOrdersByBranchID;
        nameOperationStr = "serviceOrdersByBranchID";
      } else if (!!companyID || !!me.companyID) {
        objOperation = {
          companyID: companyID || me.companyID,
        };
        nameOperation = serviceOrdersByCompanyID;
        nameOperationStr = "serviceOrdersByCompanyID";
      }

      const serviceOrdersData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          filter: {
            deletedAt: { attributeExists: false },
            start: { attributeExists: true },
            end: { attributeExists: true },
          },
        })
      );
      const serviceOrdersItems =
        serviceOrdersData.data[nameOperationStr].items ?? [];

      const holidaysData = await API.graphql(
        graphqlOperation(listHolidays, {
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const holidaysItems = holidaysData.data.listHolidays.items ?? [];

      setEvents({
        loading: false,
        list: [
          ...serviceOrdersItems.map((serv) => ({
            ...serv,
            title: serv.title || serv.code,
            start: moment(serv.start).toDate(),
            end: moment(serv.end).toDate(),
          })),
          ...holidaysItems.map((item) => ({
            ...item,
            title: item.name,
            start: moment(item.date).startOf("day").toDate(),
            end: moment(item.date).endOf("day").toDate(),
            allDay: true,
            isHoliday: true,
          })),
        ],
      });
      getNames(serviceOrdersItems, setEvents);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setEvents((prev) => ({ ...prev, loading: false }));
    }
  };

  const funcUpdateServiceOrder = async (values) => {
    try {
      if (values?.title) values.slug = convertToSlug(values.title);
      const serviceOrderUpdated = (
        await API.graphql({
          query: updateServiceOrder.replaceAll("__typename", ""),
          variables: {
            input: {
              ...values,
            },
          },
        })
      )?.data.updateServiceOrder;

      if (!!values.status) {
        await API.graphql({
          query: createServiceOrderLog.replaceAll("__typename", ""),
          variables: {
            input: {
              serviceOrderID: values.id,
              userID: me.id,
              status: values.status,
              companyID: serviceOrderUpdated.companyID,
            },
          },
        });
      }
    } catch (err) {
      console.log("Update Service Order", err);
      toast.error(strings.responses.errorUpdated);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchAvailableEvents();
    setSelectedEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchID, companyID]);

  useEffect(() => {
    if (debounceSelectedEvent) {
      funcUpdateServiceOrder(debounceSelectedEvent);
      setDebounceEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSelectedEvent]);

  const value = {
    events,
    availableEvents,
    draggedEvent,
    setDraggedEvent,
    selectedEvent,
    selectEvent,
    createEvent,
    deleteEvent,
    updateEvent,
    moveEvent,
    selectUserExecutor,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetch: "Error al consultar órdenes de servicio",
      errorUpdated: "Error al actualizar orden de servicio",
    },
    validations: {
      endBeforeStart: "La fecha final debe ser mayor a la fecha inicio",
    },
  },
});
